import React, { useContext, useEffect, useState } from "react";
import {
  Plan2PlateDrawer,
  Plan2PlateDrawerHeader,
  usePlan2PlateDisclosure,
} from "./Plan2PlateDrawer";
import { SmallIconButton } from "./SmallIconButton";
import { cn } from "../lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { DrawerHistoryContext } from "../contexts";
import { CreatePurchaseDrawer } from "./CreatePurchaseDrawer";
import { invalidatePurchases, seedPurchase } from "../queries/caches";
import Food from "../data/food";

export interface ProductChoice {
  id: number | null; // null = Planwert
  label: string;
  kcal?: number;
}

export function ProductPickerDrawer(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  choices: ProductChoice[];
  selectedId: number | null | undefined;
  baselineKcal?: number;
  onSelect: (id: number | null) => void;
  onOpenCreate?: () => void;
  /** Food, für das ein neues Produkt angelegt werden kann. */
  createFood?: Food;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  useEffect(() => {
    if (!props.open) setSearchTerm("");
  }, [props.open]);

  const queryClient = useQueryClient();
  const drawerHistoryContext = useContext(DrawerHistoryContext);
  const createDisclosure = usePlan2PlateDisclosure(
    drawerHistoryContext,
    "create-purchase",
  );
  const createEnabled = !!props.createFood || !!props.onOpenCreate;

  const filtered = props.choices.filter((c) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return c.label.toLowerCase().includes(s);
  });

  return (
    <>
      <Plan2PlateDrawer
        onClose={props.onClose}
        open={props.open}
        header={<Plan2PlateDrawerHeader heading={props.title} />}
        body={
          <div className="flex w-full flex-col gap-4 pb-6">
            <div className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Produkte filtern"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-9 flex-1 rounded-card border border-line bg-surface-card px-3 text-sm text-ink transition-colors focus:border-teal focus:outline-none"
              />
              {createEnabled && (
                <SmallIconButton
                  icon="purchase"
                  onClick={props.onOpenCreate ?? createDisclosure.onOpen}
                  label="Neues Produkt anlegen"
                />
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              {filtered.map((c) => {
                const isSelected = c.id === props.selectedId;
                const diff =
                  c.id !== null && props.baselineKcal !== undefined
                    ? Math.round((c.kcal ?? 0) - props.baselineKcal)
                    : undefined;
                return (
                  <button
                    key={c.id === null ? "plan" : `p-${c.id}`}
                    type="button"
                    data-cy="product-choice"
                    onClick={() => props.onSelect(c.id)}
                    className={cn(
                      "w-full rounded-card border p-3 text-left shadow-card transition-colors",
                      isSelected
                        ? "border-teal bg-teal/5"
                        : "border-line bg-surface-card hover:bg-surface",
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-ink">{c.label}</p>
                      {diff !== undefined && (
                        <span
                          className={cn(
                            "shrink-0 text-xs font-semibold",
                            diff >= 0 ? "text-gold" : "text-teal",
                          )}
                        >
                          {diff >= 0 ? "+" : ""}
                          {diff} kcal
                        </span>
                      )}
                    </div>
                    {c.kcal !== undefined && (
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {Math.round(c.kcal)} kcal
                      </p>
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-ink-muted">
                  Keine Produkte gefunden.
                </p>
              )}
            </div>
          </div>
        }
      />
      {!props.onOpenCreate && props.createFood && (
        <CreatePurchaseDrawer
          open={createDisclosure.isOpen}
          onClose={createDisclosure.onClose}
          food={props.createFood}
          onSubmit={(newPurchase) => {
            seedPurchase(queryClient, newPurchase);
            invalidatePurchases(queryClient).then(() =>
              setTimeout(() => props.onSelect(newPurchase.id), 100),
            );
          }}
        />
      )}
    </>
  );
}
